import { esDe } from '../motor/errores'
import type { Campo } from '../motor/esquema'

/**
 * La pestaña «Ajustes» del constructor: un menú lateral con secciones que **salen de cómo
 * agrupa el núcleo los campos del formulario** —sus plegables («Qué pasa al enviar»,
 * «Cuándo se admiten respuestas»…)—, no de una lista escrita aquí. Un grupo o una lista con
 * nombre dentro de un plegable (la confirmación, los avisos por correo) es su propia
 * sección; los campos sueltos de un plegable van juntos con el nombre del plegable; y lo que
 * no está en ningún plegable (el identificador, el estado) va en «General».
 *
 * Funciones puras, con su prueba (`ajustes.test.ts`).
 */
export type Seccion = { clave: string; etiqueta: string; descripcion?: string; campos: Campo[] }

/** Lo que no es un ajuste: los campos los edita el lienzo, el nombre la cabecera, y la plantilla solo vale al crear. */
export const FUERA_DE_AJUSTES = new Set(['campos', 'nombre', 'plantilla'])

const aClave = (texto: string, i: number) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || `seccion-${i}`

const esSeccionPropia = (c: Campo) =>
  Boolean(c.nombre) && (c.tipo === 'group' || c.tipo === 'array') && Boolean(c.etiqueta)

export const seccionesDeAjustes = (campos: Campo[]): Seccion[] => {
  const general: Campo[] = []
  const secciones: Seccion[] = []
  campos.forEach((c, i) => {
    if (c.nombre && FUERA_DE_AJUSTES.has(c.nombre)) return
    if (c.tipo !== 'collapsible') {
      general.push(c)
      return
    }
    // Un plegable: sus grupos y listas con nombre, cada uno su sección; lo suelto, junto.
    let sueltos: Campo[] = []
    const cerrarSueltos = () => {
      if (sueltos.length === 0) return
      secciones.push({
        clave: aClave(c.etiqueta, i),
        etiqueta: c.etiqueta,
        ...(c.descripcion ? { descripcion: c.descripcion } : {}),
        campos: sueltos,
      })
      sueltos = []
    }
    for (const hijo of c.campos ?? []) {
      if (hijo.nombre && FUERA_DE_AJUSTES.has(hijo.nombre)) continue
      if (esSeccionPropia(hijo)) {
        cerrarSueltos()
        secciones.push({
          clave: hijo.nombre!,
          etiqueta: hijo.etiqueta,
          ...(hijo.descripcion ? { descripcion: hijo.descripcion } : {}),
          campos: [hijo],
        })
      } else sueltos.push(hijo)
    }
    cerrarSueltos()
  })
  // Claves únicas aunque dos secciones se llamen igual.
  const vistas = new Set<string>(general.length > 0 ? ['general'] : [])
  const unicas = secciones.map((s) => {
    let clave = s.clave
    for (let n = 2; vistas.has(clave); n++) clave = `${s.clave}-${n}`
    vistas.add(clave)
    return { ...s, clave }
  })
  return general.length > 0
    ? [{ clave: 'general', etiqueta: 'General', campos: general }, ...unicas]
    : unicas
}

/** Las rutas con nombre de una sección (lo que puede tener un error), bajando por lo que no tiene nombre. */
const rutasDe = (campos: Campo[]): string[] =>
  campos.flatMap((c) => (c.nombre ? [c.nombre] : rutasDe(c.campos ?? [])))

/** En qué sección cae el error de esta ruta («notificaciones.0.para» → la de los avisos), o `null` si en ninguna. */
export const seccionDelError = (ruta: string, secciones: Seccion[]): null | string =>
  secciones.find((s) => rutasDe(s.campos).some((r) => esDe(ruta, r)))?.clave ?? null
